import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, IconButton, InputAdornment, Popover } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';

import { MAX_CALENDAR_DATE, MIN_CALENDAR_DATE } from 'constants/date';
import { PendingChangeKind } from 'constants/pendingChanges';
import { COLOR_PALETTE } from 'constants/ui';
import { getConditionalObject } from 'utils/data';

import CancelIcon from '../../icons/CancelIcon';
import DateIcon from '../../icons/DateIcon';
import FieldWrapper from '../FieldWrapper';
import TextFieldCDS from '../TextFieldCDS';

import {
  INVALID_DATE_INPUT_ATTRIBUTE,
  TIME_SELECTION_FINISH_STATE,
} from './DateFieldCDS.constants';
import {
  calendarIconWrapperStyles,
  calendarPopoverAnchorOrigin,
  calendarPopoverSlotProps,
  calendarPopoverTransformOrigin,
  dateCalendarStyles,
  pickerContentRowStyles,
  timeClockStyles,
} from './DateFieldCDS.styles';
import {
  areValuesEqual,
  computeEmitDate,
  focusEmptyMaskStart,
  formatDateToMask,
  handleMaskKeyDown,
  handleMaskPaste,
  isMaskFullyEmpty,
  mergeCalendarSelectionWithMask,
  mergeTimeSelectionWithMask,
  parseMaskToDate,
  resolveMaskTemplate,
  toDayjsOrNull,
} from './DateFieldCDS.utils';

const DateFieldCDS = ({
  className = '',
  value,
  name = '',
  columns = 1,
  dataTestId,
  width = '100%',
  withTime = false,
  clearable = false,
  format = null,
  onBlur,
  onChange,
  onFocus,
  maxDate = MAX_CALENDAR_DATE,
  minDate = MIN_CALENDAR_DATE,
  disabled = false,
  containerClassName = '',
  isFieldChanged = false,
  error = false,
  shouldDisableDate,
  isHidden = false,
  type,
  views,
  ...props
}) => {
  const template = resolveMaskTemplate(format, withTime);

  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);
  const lastEmittedValueRef = useRef(value);

  const [maskValue, setMaskValue] = useState(() => formatDateToMask(value, template));
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [hasInvalidInput, setHasInvalidInput] = useState(false);

  const minDateValue = useMemo(() => toDayjsOrNull(minDate), [minDate]);
  const maxDateValue = useMemo(() => toDayjsOrNull(maxDate), [maxDate]);
  const parentDateValue = useMemo(() => toDayjsOrNull(value), [value]);

  const emit = useCallback(
    nextValue => {
      const finalValue = computeEmitDate(nextValue, minDate, maxDate, value);

      lastEmittedValueRef.current = finalValue;
      setHasInvalidInput(false);
      onChange(finalValue, name);

      return finalValue;
    },
    [maxDate, minDate, name, onChange, value],
  );

  const applyMask = useCallback(
    ({ mask, nextCursorPosition }) => {
      pendingCursorRef.current = nextCursorPosition;
      setMaskValue(mask);

      if (isMaskFullyEmpty(mask, template)) {
        setHasInvalidInput(false);

        if (!isNull(lastEmittedValueRef.current)) {
          emit(null);
        }

        return;
      }

      const parsed = parseMaskToDate(mask, template);

      if (isNull(parsed) || areValuesEqual(parsed, lastEmittedValueRef.current, template)) {
        return;
      }

      const emitted = emit(parsed);

      if (!areValuesEqual(emitted, parsed, template)) {
        pendingCursorRef.current = null;
        setMaskValue(formatDateToMask(emitted, template));
      }
    },
    [emit, template],
  );

  const onInputKeyDown = useCallback(
    event => {
      const result = handleMaskKeyDown({ event, mask: maskValue, template });

      if (!isNull(result)) {
        applyMask(result);
      }
    },
    [applyMask, maskValue, template],
  );

  const onInputPaste = useCallback(
    event => {
      const result = handleMaskPaste({ event, mask: maskValue, template });

      if (!isNull(result)) {
        applyMask(result);
      }
    },
    [applyMask, maskValue, template],
  );

  const onInputFocus = useCallback(
    event => {
      if (isMaskFullyEmpty(maskValue, template)) {
        focusEmptyMaskStart(inputRef, template);
      }

      if (isNil(onFocus)) {
        return;
      }

      const parsed = parseMaskToDate(maskValue, template);

      onFocus(parsed, name, event);
    },
    [maskValue, name, onFocus, template],
  );

  const onInputBlur = useCallback(
    event => {
      const isMaskEmpty = isMaskFullyEmpty(maskValue, template);
      const parsed = isMaskEmpty ? null : parseMaskToDate(maskValue, template);

      setHasInvalidInput(!isMaskEmpty && isNull(parsed));

      if (isNil(onBlur)) {
        return;
      }

      onBlur(parsed, name, event);
    },
    [maskValue, name, onBlur, template],
  );

  const onCalendarButtonClick = useCallback(event => {
    setCalendarAnchor(event.currentTarget);
  }, []);

  const onCalendarClose = useCallback(() => {
    setCalendarAnchor(null);
  }, []);

  const onCalendarChange = useCallback(
    selectedDate => {
      const nextDate = mergeCalendarSelectionWithMask({
        selectedDate,
        mask: maskValue,
        template,
      });

      const emitted = emit(nextDate);

      setMaskValue(formatDateToMask(emitted, template));

      if (!withTime) {
        setCalendarAnchor(null);
      }
    },
    [emit, maskValue, template, withTime],
  );

  const onTimeChange = useCallback(
    (selectedTime, selectionState) => {
      const nextDate = mergeTimeSelectionWithMask({
        selectedTime,
        mask: maskValue,
        template,
      });

      const emitted = emit(nextDate);

      setMaskValue(formatDateToMask(emitted, template));

      if (selectionState === TIME_SELECTION_FINISH_STATE) {
        setCalendarAnchor(null);
      }
    },
    [emit, maskValue, template],
  );

  const onClearClick = useCallback(
    event => {
      event.stopPropagation();
      pendingCursorRef.current = 0;
      setMaskValue(formatDateToMask(null, template));

      if (!isNull(lastEmittedValueRef.current)) {
        emit(null);
      }

      if (!isNull(inputRef.current)) {
        inputRef.current.focus();
      }
    },
    [emit, template],
  );

  const inputProps = useMemo(
    () => ({
      onKeyDown: onInputKeyDown,
      onPaste: onInputPaste,
      inputMode: 'numeric',
      autoComplete: 'off',
      spellCheck: 'false',
      ...getConditionalObject(hasInvalidInput, { [INVALID_DATE_INPUT_ATTRIBUTE]: true }),
    }),
    [hasInvalidInput, onInputKeyDown, onInputPaste],
  );

  const showClearButton = clearable && !disabled && !isMaskFullyEmpty(maskValue, template);

  const InputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end" sx={calendarIconWrapperStyles}>
          {showClearButton && (
            <IconButton aria-label="clear" onClick={onClearClick}>
              <CancelIcon color={COLOR_PALETTE.GRAY[6]} size={18} />
            </IconButton>
          )}
          <IconButton
            aria-label="open calendar"
            disabled={disabled}
            onClick={onCalendarButtonClick}
          >
            <DateIcon color={COLOR_PALETTE.GRAY[6]} size={22} />
          </IconButton>
        </InputAdornment>
      ),
    }),
    [disabled, onCalendarButtonClick, onClearClick, showClearButton],
  );

  useEffect(() => {
    if (areValuesEqual(value, lastEmittedValueRef.current, template)) {
      return;
    }

    lastEmittedValueRef.current = value;
    setMaskValue(formatDateToMask(value, template));
  }, [value, template]);

  useEffect(() => {
    if (isNull(pendingCursorRef.current) || !inputRef.current) {
      return;
    }

    const position = pendingCursorRef.current;
    pendingCursorRef.current = null;
    inputRef.current.setSelectionRange(position, position);
  }, [maskValue]);

  return (
    <FieldWrapper
      className={containerClassName}
      columns={columns}
      isHidden={isHidden}
      width={width}
    >
      <TextFieldCDS
        {...props}
        InputProps={InputProps}
        className={clsx(className, 'date-picker')}
        dataTestId={dataTestId}
        disabled={disabled}
        error={error}
        inputProps={inputProps}
        inputRef={inputRef}
        isFieldChanged={isFieldChanged}
        name={name}
        value={maskValue}
        onBlur={onInputBlur}
        onChange={noop}
        onFocus={onInputFocus}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Popover
          anchorEl={calendarAnchor}
          anchorOrigin={calendarPopoverAnchorOrigin}
          open={!isNull(calendarAnchor)}
          slotProps={calendarPopoverSlotProps}
          transformOrigin={calendarPopoverTransformOrigin}
          onClose={onCalendarClose}
        >
          <Box sx={pickerContentRowStyles}>
            <DateCalendar
              maxDate={maxDateValue}
              minDate={minDateValue}
              openTo={views?.[0]}
              shouldDisableDate={shouldDisableDate}
              sx={dateCalendarStyles}
              value={parentDateValue}
              views={views}
              onChange={onCalendarChange}
            />
            {withTime && (
              <MultiSectionDigitalClock
                ampm={false}
                sx={timeClockStyles}
                value={parentDateValue}
                onChange={onTimeChange}
              />
            )}
          </Box>
        </Popover>
      </LocalizationProvider>
    </FieldWrapper>
  );
};

DateFieldCDS.propTypes = {
  label: PropTypes.string.isRequired,
  allowKeyboardControl: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  columns: PropTypes.number,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  format: PropTypes.string,
  margin: PropTypes.string,
  maxDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  minDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  views: PropTypes.arrayOf(PropTypes.oneOf(['year', 'month', 'day'])),
  width: PropTypes.string,
  withTime: PropTypes.bool,
  clearable: PropTypes.bool,
  shouldDisableDate: PropTypes.func,
  isFieldChanged: PropTypes.bool,
  fieldChangeKind: PropTypes.oneOf(Object.values(PendingChangeKind)),
  isHidden: PropTypes.bool,
  error: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
};

export default memo(DateFieldCDS);
